<template>

    <div class="fetch">

        <div class="fetch__container">

            <div class="fetch__top">

                <img src="/img/fetch/logo-large-white.svg" v-show="done || isDesktop" class="fetch__top__logo">
                <tips-fetch v-show="fetching && !error"></tips-fetch>

            </div>

            <div class="fetch__body">

                <lottie-loader v-if="fetching && !error" :size="40"></lottie-loader>
                <lottie-check v-if="done && !error" :size="40"></lottie-check>
                <img src="/img/fetch/loader-error.svg" v-show="error" class="fetch__error__icon">

                <p class="text-xlSemibold-2 mt__16">{{ computedTitle }}</p>

                <p class="text-baseMedium mt__4">{{ computedSubtitle }}</p>

                <scrolling-senders class="mt__32"
                                   v-if="fetching"
                                   :samples="samples"></scrolling-senders>

            </div>

            <div class="fetch__bottom">

                <template v-if="fetching && !error && showCheckboxNotif">

                    <div class="fetch__notif">

                        <div class="fetch__notif__left">
                            <p class="text-baseSemibold-2">{{ $t('fetch_switch_title') }}</p>
                            <p class="text-baseRegular-2">{{ $t('fetch_switch_description') }}</p>
                        </div>
                        <div class="fetch__notif__right">
                            <img :src="isNotifChecked ? '/img/fetch/switch-on.svg' : '/img/fetch/switch-off.svg'"
                                 @click="notifCheckUpdate"
                                 class="fetch__notif__checkbox">
                        </div>
                    </div>

                    <button v-show="fetching"
                            class="fetch__btn__settings text-lSemibold mt__24"
                            @click="goSettings">{{ $t('fetch_settings') }}
                    </button>

                </template>

                <template v-if="doneWithNewsletters">

                    <div class="fetch__btn__clean__container">
                        <img src="/img/foxy/middle-smile.png" class="fetch__btn__clean__foxy">
                        <button class="text-xlSemibold-2 color__white fetch__btn__clean" @click="goClean">
                            {{ $t('partial_fetch_scan_done_cta') }}
                        </button>
                    </div>

                    <button class="fetch__btn__settings text-lSemibold mt__24"
                            @click="goAddIndox">{{ $t('fetch_done_add_inbox_cta') }}
                    </button>
                </template>

                <template v-if="error">

                    <button class="text-xlSemibold-2 color__white fetch__btn__clean" @click="restart">
                        {{ $t('fetch_error_cta') }}
                    </button>
                </template>

            </div>

            <bottom-sheet-fetch-notifications ref="btmsht"
                                              @permissionResponse="permissionResponseFromBottomSheet"></bottom-sheet-fetch-notifications>

            <bottom-sheet-accept-notifications ref="btmshtAccept"></bottom-sheet-accept-notifications>

            <bottom-sheet-refuse-notifications ref="btmshtRefuse"></bottom-sheet-refuse-notifications>

            <bottom-sheet-add-mailbox></bottom-sheet-add-mailbox>

        </div>

    </div>

</template>

<script>
import Vuex from 'vuex'

import Api from '@/api'

import Storage from '@/helpers/storage'

import I18n from '@/modules/i18n'
import Notifs from '@/modules/notifications'
import Tag from '@/modules/tag'
import UserFlow from '@/modules/userFlow'

import BottomSheetAcceptNotifications from '@/components/bottomSheet/BottomSheetAcceptNotifications'
import BottomSheetAddMailbox from '@/components/bottomSheet/BottomSheetAddMailbox'
import BottomSheetFetchNotifications from '@/components/bottomSheet/BottomSheetFetchNotifications'
import BottomSheetRefuseNotifications from '@/components/bottomSheet/BottomSheetRefuseNotifications'
import CheckboxCircle from '@/components/inputs/CheckboxCircle'
import LottieLoader from '@/components/lottie/LottieLoader'
import LottieCheck from '@/components/lottie/LottieCheck'
import ScrollingSenders from '@/components/ScrollingSenders.vue'
import TipsFetch from '@/components/TipsFetch'

export default {

    name: 'fetch-v2',

    components: {
        BottomSheetAcceptNotifications,
        BottomSheetAddMailbox,
        BottomSheetFetchNotifications,
        BottomSheetRefuseNotifications,
        CheckboxCircle,
        LottieLoader,
        LottieCheck,
        ScrollingSenders,
        TipsFetch
    },

    watch: {
        fetching: function (bool) {
            if (!bool && this.showBottomSheetAddMailbox) {
                this.updateShowBottomSheetAddMailbox(false)
            }
        }
    },

    computed: {

        ...Vuex.mapState({
            fetching: state => state.fetching,
            showBottomSheetAddMailbox: state => state.showBottomSheetAddMailbox,
            newsletters: state => state.newsletters
        }),

        computedTitle() {
            if (this.error) {
                return this.$t('fetch_error_title')
            } else if (this.fetching) {
                return this.$t('fetch_title')
            } else if (this.done) {
                return this.$t('fetch_done_title')
            }
        },

        computedSubtitle() {
            if (this.error) {
                return this.$t('fetch_error_text')
            } else if (this.fetching) {
                return this.$t('fetch_description')
            } else if (this.done) {
                return this.$t('fetch_done_description')
            }
        },

        doneWithNewsletters() {
            return this.done && !this.error && this.newsletters.subscriptions.length > 0
        }
    },

    data() {
        return {
            error: false,
            samples: [],
            isNotifChecked: false,
            done: false,
            fetchLoopFirstTime: true,
            showCheckboxNotif: false
        }
    },

    mounted() {
        Tag.pageViewGA(this, 'fetch')

        UserFlow.setPage(this, 'fetch')

        this.notifEmailLanguage()

        this.startDeferredAction()

        this.fetch()

        this.showCheckboxNotif = Notifs.isGranted()
    },

    methods: {

        ...Vuex.mapActions([
            'updateFetching',
            'updateShowBottomSheetAddMailbox',
            'refreshNewsletters'
        ]),

        fetch() {

            this.refreshNewsletters(this.asyncFetchMeantime)
                .then(result => {
                    Tag.pageViewAll(this, 'fetch_done')
                    if (result.subscriptions.length === 0) {
                        this.$router.push({path: '/fetch-end-empty'})
                    } else {
                        this.done = true
                    }
                })
                .catch(() => {
                    this.error = true
                })
        },

        restart() {
            this.error = false
            this.samples = []
            this.done = false
            this.fetch()
        },

        asyncFetchMeantime(data) {

            if (this.fetchLoopFirstTime) {
                if (!data.done && this.mustAskPermission()) {

                    this.$refs.btmsht.open()

                }
                this.fetchLoopFirstTime = false
            }

            this.samples = data.samples
        },

        mustAskPermission() {
            return Notifs.haveNotificationsNeverBeenRequested()
        },

        permissionResponseFromBottomSheet(permission) {

            switch (permission) {
                case 'granted':
                    this.showCheckboxNotif = false
                    this.$refs.btmshtAccept.open()
                    break
                case 'default':
                    this.showCheckboxNotif = true
                    break
                case 'denied':
                    this.showCheckboxNotif = true
                    this.$refs.btmshtRefuse.open()
                    break
            }

        },

        goClean() {
            Tag.clickGA(this, 'next')
            this.$router.push({path: '/newsletters'})
                .catch(() => {
                })
        },

        goSettings() {
            Tag.clickGA(this, 'settings')
            this.$router.push({path: '/settings'})
        },

        goAddIndox() {
            Tag.clickGA(this, 'add_inbox')
            this.$router.push({path: '/add-inbox'})
        },

        goStats() {
            Tag.clickGA(this, 'stats')
            this.$router.push({path: '/stats'})
        },

        notifEmailLanguage() {

            Api.user.updateFetchLanguage(I18n.get(this))
                .catch(() => {
                })
        },

        notifCheckUpdate() {

            const bool = !this.isNotifChecked


            if (!Notifs.isDenied()) {

                Notifs.update({important: bool, news: bool})
                    .then(settings => {
                        this.isNotifChecked = settings.notifications.push.news || settings.notifications.push.important
                    })
                    .catch(() => {
                        this.isNotifChecked = false
                    })

            } else {

                this.$refs.btmshtRefuse.open()
            }

        },

        startDeferredAction() {
            setTimeout(() => {
                if (this.fetching) {
                    this.updateShowBottomSheetAddMailbox(true)
                }
            }, 3000)
        }
    }
}
</script>

<style lang="scss" scoped>

.fetch {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    background-color: $aquamarine;
    background-repeat: no-repeat;
    background-position: bottom;
    background-size: cover;
    background-image: url("/img/fetch/illustration-fetch-background-mobile.svg");
    @media screen and (min-width: $above-tablet-length) {
        background-image: url("/img/fetch/illustration-fetch-desktop.svg");
    }


    &__container {
        display: flex;
        flex-direction: column;
        flex: 1;
        max-width: 480px;
        @media screen and (min-width: $above-tablet-length) {
            padding: 16px 0 24px;
        }
    }

    &__top {
        display: flex;
        flex-direction: column;
        padding: 16px;
        min-height: 134px;
        justify-content: center;

        &__logo {
            height: 40px;

            @media screen and (min-width: $above-tablet-length) {
                margin-bottom: 30px;
            }
        }
    }

    &__body {
        display: flex;
        flex-direction: column;
        align-items: center;
        flex-grow: 1;
        padding: 0 16px;
        @media screen and (min-width: $above-tablet-length) {
            padding-top: 26px;
        }
    }

    &__bottom {
        padding: 0 16px 24px;
    }

    &__notif {
        display: flex;
        flex-direction: row;
        padding: 16px;
        border-radius: 12px;
        background-color: $white;

        &__left {
            display: flex;
            flex-direction: column;
            flex: 1;
            text-align: start;
        }

        &__right {
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 60px;
        }
    }

    &__btn {
        &__settings {
            background-color: inherit;
        }

        &__clean {
            width: 100%;
            padding: 18px;
            border-radius: 72px;
            background-color: $midnight;

            &__container {
                display: flex;
                align-items: center;
                position: relative;
            }

            &__foxy {
                position: absolute;
                top: -79px;
                height: 87px;
            }
        }
    }

    &__error {
        &__icon {
            width: 40px;
            object-fit: contain;
            @media screen and (min-width: $above-tablet-length) {
                margin-top: 70px;
            }
        }
    }
}

</style>
